// Developer → one test run's report. This is where the loop closes: the dev
// reviews each failed / errored / blocked case, fixes it, and marks it Passed.
//
// The dev's verdict NEVER overwrites the agent's result — it's stored alongside
// it. That's what lets the tester's next re-run tell "dev says fixed → verify"
// apart from "genuinely passed → skip", and it's why each row shows both what
// the machine found and what the developer claims.
//
// The table itself is shared with the PM's read-only view (BatchReportTable), so
// the two can't drift apart on what a status means. It pages and filters on the
// server; this page used to render a raw <table> of every case in the batch with
// a client-side text filter and no pagination at all.

import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Loader2, WifiOff, CheckCircle2, XCircle, Bug, Boxes, RotateCw, Clock,
} from "lucide-react";
import Aurora from "@/components/common/Aurora";
import BatchReportTable from "@/components/shared/BatchReportTable";
import DownloadReportButton from "@/components/developer/DownloadReportButton";
import { StatusBadge } from "@/components/ui/Badge";
import { cn } from "@/lib/cn";
import { getDevBatchReport } from "@/api/developer";
import { formatDateTime } from "@/lib/dates";

const fmtWhen = (v) => {
  if (!v) return "—";
  return formatDateTime(v);
};

export default function DevBatchReport() {
  const { id, kind, batchId } = useParams();
  const [header, setHeader] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // The shared table fetches its own pages; this only needs the batch summary
  // and the whole-batch counts, both of which come back with any page.
  const loadHeader = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      setHeader(await getDevBatchReport(kind, batchId, { limit: 1 }));
    } catch (e) {
      setError((e && e.message) || "Couldn't load this report.");
    } finally {
      setLoading(false);
    }
  }, [kind, batchId]);

  // Refresh the counts WITHOUT flipping `loading`. Handing loadHeader to the table
  // as onChanged put this page back into the full-page spinner after every verdict,
  // which unmounted <BatchReportTable> and threw away its page, search and filters —
  // in a workflow whose whole point is marking many rows in a row. A failed refresh
  // is swallowed on purpose: the table surfaces its own write errors, and setting
  // `error` here would unmount the table just as effectively as the spinner did.
  const refreshCounts = useCallback(async () => {
    try {
      setHeader(await getDevBatchReport(kind, batchId, { limit: 1 }));
    } catch {
      /* keep the stale header rather than tearing the table down */
    }
  }, [kind, batchId]);

  useEffect(() => { loadHeader(); }, [loadHeader]);

  // Stable identity, or the table's loader would re-fire on every render.
  const fetchPage = useCallback(
    (params) => getDevBatchReport(kind, batchId, params),
    [kind, batchId],
  );

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center text-slate-400">
        <Loader2 className="animate-spin" /> <span className="ml-2">Loading report…</span>
      </div>
    );
  }

  if (error) {
    return (
      <>
        <Link to={`/developer/projects/${id}`} className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-brand-600 dark:text-slate-400">
          <ArrowLeft size={16} /> Back to project
        </Link>
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-2.5 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
          <WifiOff size={15} /> <span className="flex-1">{error}</span>
          <button type="button" onClick={loadHeader}
            className="inline-flex items-center gap-1 rounded-lg border border-amber-300 px-2.5 py-1 text-xs font-medium hover:bg-amber-100 dark:border-amber-500/30 dark:hover:bg-amber-500/20">
            <RotateCw size={12} /> Retry
          </button>
        </div>
      </>
    );
  }

  const batch = header?.batch;
  // Counts describe the whole batch, computed server-side. Deriving them from
  // the rows on screen would turn "12 need a fix" into "12 on this page".
  const counts = header?.counts || {};

  return (
    <>
      <Link to={`/developer/projects/${id}`} className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-brand-600 dark:text-slate-400">
        <ArrowLeft size={16} /> Back to {header?.project?.name || "project"}
      </Link>

      {/* header */}
      <motion.div
        initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
        className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-soft dark:border-white/10 dark:bg-ink-900"
      >
        <Aurora className="opacity-50" />
        <div className="relative flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-glow">
              <Boxes size={22} />
            </span>
            <div className="min-w-0">
              <h1 className="font-display text-xl font-bold tracking-tight text-slate-900 dark:text-white">
                {batch?.name || `Run ${String(batchId).slice(0, 8)}`}
              </h1>
              <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-400">
                {batch && <StatusBadge status={batch.status} />}
                <span>{fmtWhen(batch?.created_at)}</span>
                <span>· {kind === "extension" ? "Extension" : "Exe agent"}</span>
                {batch?.rerun_count > 0 && (
                  <span className="inline-flex items-center gap-1 text-brand-600 dark:text-brand-300">
                    <RotateCw size={11} /> re-run ×{batch.rerun_count}
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* Rebuilt on every click, so it always carries the verdicts recorded
              on this page — including ones made seconds ago. */}
          <div className="flex flex-col items-end gap-1">
            <DownloadReportButton
              kind={kind}
              batchId={batchId}
              disabled={!counts.total}
              disabledHint="This run has no cases to report on yet"
            />
            <span className="text-[0.6875rem] text-slate-400">
              Always the latest results
            </span>
          </div>
        </div>

        <div className="relative mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { label: "Cases", value: counts.total ?? 0, tone: "text-sky-500 bg-sky-500/10", Icon: Boxes },
            { label: "Need a fix", value: counts.needs_fix ?? 0, tone: "text-rose-500 bg-rose-500/10", Icon: Bug },
            { label: "Awaiting re-run", value: counts.awaiting_rerun ?? 0, tone: "text-brand-500 bg-brand-500/10", Icon: Clock },
            { label: "Verified", value: counts.verified ?? 0, tone: "text-emerald-500 bg-emerald-500/10", Icon: CheckCircle2 },
          ].map((s) => (
            <div key={s.label} className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50/70 px-4 py-3 dark:border-white/5 dark:bg-white/5">
              <span className={cn("grid h-9 w-9 shrink-0 place-items-center rounded-lg", s.tone)}><s.Icon size={16} /></span>
              <div className="min-w-0">
                <p className="truncate text-xs text-slate-400">{s.label}</p>
                <p className="text-lg font-bold text-slate-900 dark:text-white">{s.value}</p>
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      {counts.still_failing > 0 && (
        <div className="mt-5 flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50/70 px-4 py-2.5 text-sm text-rose-800 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-200">
          <XCircle size={15} />
          {counts.still_failing} case{counts.still_failing === 1 ? "" : "s"} you marked Passed
          {counts.still_failing === 1 ? " is" : " are"} still failing after the re-run.
        </div>
      )}

      <div className="mt-6">
        <BatchReportTable
          kind={kind}
          batchId={batchId}
          fetchPage={fetchPage}
          canJudge
          projectId={header?.project?.id}
          onChanged={refreshCounts}
        />
      </div>
    </>
  );
}
